import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useGlobalStore } from '../../application/store/store.tsx';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import AvailableDrugs from './components/AvailableDrugs.tsx';
import AvailableStorages from './components/AvailableStorages.tsx';
import Patient from './components/Patient.tsx';
import Note from './components/Note.tsx';
import Summary from './components/Summary.tsx';
import Sign from './components/Sign.tsx';
import TextField from '@mui/material/TextField';
import {
    Alert,
    Box,
    Button,
    InputAdornment,
    Step,
    StepLabel,
    Stepper,
} from '@mui/material';
import SignedSuccess from './components/SignedSuccess.tsx';
import {
    TransactionOverview,
    TransactionType,
} from '../../application/types/transactionType.ts';
import IconTextField from '../../common/components/IconTextfield.tsx';
import KeyboardListener from '../../common/components/KeyboardListener.tsx';
import BarCodeOnOffSwitch from '../../common/components/BarCodeOnOffSwitch.tsx';
import Visibility from '../../common/components/Visibility.tsx';
import ScanningDetector from './components/ScanningDetector.tsx';
import { StorageType } from '../../application/types/storageType.ts';

const steps = [
    'Läkemedel',
    'Läkemedelsskåp',
    'Akutväska',
    'Patient',
    'Anteckning',
    'Sammanfattning',
];

const ChainedTransaction = () => {
    const [isStepperOpen, setIsStepperOpen] = useState(false);

    const toggleStepper = () => {
        setIsStepperOpen(!isStepperOpen);
    };

    const selectedDrug = useGlobalStore((state) => state.selectedDrug);
    const setSelectedDrug = useGlobalStore((state) => state.setSelectedDrug);

    const selectedStorage = useGlobalStore((state) => state.selectedStorage);
    const setSelectedStorage = useGlobalStore(
        (state) => state.setSelectedStorage
    );

    const selectedEmergency = useGlobalStore(
        (state) => state.selectedEmergency
    );
    const setSelectedEmergency = useGlobalStore(
        (state) => state.setSelectedEmergency
    );

    const quantityChanged = useGlobalStore((state) => state.quantityChanged);
    const setQuantityChanged = useGlobalStore(
        (state) => state.setQuantityChanged
    );

    const patientData = useGlobalStore((state) => state.patientData);

    const note = useGlobalStore((state) => state.note);

    const ambulanceOverview = useGlobalStore(
        (state) => state.ambulanceOverview
    );

    const storageRemaining = useGlobalStore((state) => state.storageRemaining);
    const setstorageRemaining = useGlobalStore(
        (state) => state.setstorageRemaining
    );

    const emergencyRemaining = useGlobalStore(
        (state) => state.emergencyRemaining
    );
    const setemergencyRemaining = useGlobalStore(
        (state) => state.setemergencyRemaining
    );

    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );
    const resetStorage = useGlobalStore((state) => state.resetAvailableStorage);
    const resetPatient = useGlobalStore((state) => state.resetPatient);
    const resetNote = useGlobalStore((state) => state.resetNote);
    const { setDoubleSignature } = useGlobalStore();
    const formRegistrationMethod = useGlobalStore(
        (state) => state.formRegistrationMethod
    );
    const resetBarcodeState = useGlobalStore(
        (state) => state.resetBarCodeState
    );

    useEffect(() => {
        return () => {
            resetSelectedDrug();
            resetStorage();
            resetPatient();
            resetNote();
            setDoubleSignature(false);
        };
    }, [
        resetSelectedDrug,
        resetStorage,
        resetPatient,
        resetNote,
        setDoubleSignature,
    ]);

    const stepContents = [
        `${selectedDrug?.drugName ?? ''} ${selectedDrug?.drugName ? `(${selectedDrug?.concentration ?? ''} ${selectedDrug?.concentrationUnit ?? ''}, ${selectedDrug?.quantity ?? ''}ml)` : ''}`,
        `${selectedStorage?.name ?? ''}`,
        `${selectedEmergency?.name ?? ''}`,
        `${patientData?.firstname && patientData?.lastname ? `${patientData.firstname} ${patientData.lastname}` : ''}`,
        '',
        '',
    ];

    const [activeStep, setActiveStep] = useState(0);
    const [searchTermDrugs, setSearchTermDrugs] = useState<string>('');
    const [searchTermStorage, setSearchTermStorage] = useState<string>('');
    const [searchTermEmergency, setSearchTermEmergency] = useState<string>('');

    const navigate = useNavigate();

    const handleNext = () => {
        resetBarcodeState();
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = (e) => {
        e.preventDefault();
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        resetBarcodeState();
    };

    const handleNavigateBack = () => {
        // Clear all state before navigating
        resetSelectedDrug();
        resetStorage();
        resetPatient();
        resetNote();
        resetBarcodeState();
        setDoubleSignature(false);

        // Reset overview state
        useGlobalStore.getState().resetOverviews();

        navigate(NavigationRoutes.transaction);
    };

    const handleSelectDrug = (drug) => {
        const formattedDrugValue = `${selectedDrug?.drugName ?? ''} ${selectedDrug?.drugName ? `(${selectedDrug?.concentration ?? ''} ${selectedDrug?.concentrationUnit ?? ''}, ${selectedDrug?.quantity ?? ''}ml)` : ''}`;
        setSelectedDrug(drug);
        setSearchTermDrugs(formattedDrugValue);
    };

    const handleSelectStorage = (storage) => {
        const formattedStorageValue = `${selectedStorage?.name ?? ''}`;
        setSelectedStorage(storage);
        setSearchTermStorage(formattedStorageValue);
    };

    const handleSelectEmergency = (emergency) => {
        const formattedEmergencyValue = `${emergency?.name ?? ''}`;
        setSelectedEmergency(emergency);
        setSearchTermEmergency(formattedEmergencyValue);
    };

    return (
        <div>
            <KeyboardListener />
            <div className="flex flex-row justify-between items-center mb-4 text-xs sm:text-base">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNavigateBack}
                    color="primary"
                    className="!text-xs sm:!text-base"
                    size="small"
                    sx={{
                        '@media (min-width: 640px)': {
                            fontSize: 'inherit',
                            padding: '6px 16px',
                        },
                    }}
                >
                    Tillbaka
                </Button>
                <div>
                    <BarCodeOnOffSwitch />
                </div>
            </div>
            {(ambulanceOverview === TransactionOverview.formOverview ||
                ambulanceOverview ===
                    TransactionOverview.doubleSignOverview) && (
                <div className="flex min-h-screen bg-gray-50 relative">
                    {/* Stepper Toggle Button - Only visible below 1024px */}
                    <div className="hidden max-lg:block">
                        <button
                            onClick={toggleStepper}
                            className={`fixed top-1/2 -translate-y-1/2 z-50 bg-gray-200 p-2 rounded-r-md shadow-md transition-transform duration-300 ${
                                isStepperOpen
                                    ? 'translate-x-64'
                                    : 'translate-x-0'
                            }`}
                            style={{ left: 0 }}
                        >
                            {isStepperOpen ? (
                                <ChevronLeftIcon />
                            ) : (
                                <ChevronRightIcon />
                            )}
                        </button>
                    </div>

                    {/* Stepper Container */}
                    <div
                        className={`
                            lg:w-1/5 lg:block lg:relative lg:translate-x-0
                            max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-64 max-lg:z-40
                            transition-transform duration-300
                            p-5 shadow-xl rounded-lg bg-white
                            ${!isStepperOpen && 'max-lg:-translate-x-full'}
                        `}
                    >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={index}>
                                    <StepLabel>
                                        <div className="flex flex-col">
                                            <span className="text-lg font-semibold text-gray-900 text-left">
                                                {label}
                                            </span>
                                            <span className="text-sm text-gray-600 italic text-left">
                                                {stepContents[index]}
                                            </span>
                                        </div>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    {/* Overlay - Only visible below 1024px when stepper is open */}
                    {isStepperOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-30 hidden max-lg:block"
                            onClick={toggleStepper}
                        />
                    )}

                    {/* Main Content */}
                    <div className="w-full lg:w-4/5 p-2 sm:p-5 shadow-xl rounded-lg lg:ml-5 mx-2 sm:mx-5 overflow-auto">
                        {/* Navigation Buttons and Title */}
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={4}
                        >
                            <Button
                                type="button"
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                    }
                                }}
                                disabled={
                                    ambulanceOverview ===
                                        TransactionOverview.doubleSignOverview ||
                                    activeStep === 0
                                }
                                onClick={handleBack}
                                variant="text"
                                startIcon={<ArrowBackIcon />}
                                sx={{
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                }}
                            >
                                Tillbaka
                            </Button>
                            <h1 className="text-center text-lg sm:text-2xl">
                                Registrera Uttag ambulans uppdrag
                            </h1>
                            <Button
                                type="button"
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault(); // Prevent Enter to step next. Needed for barcode scanning
                                    }
                                }}
                                onClick={handleNext}
                                disabled={activeStep === steps.length - 1}
                                variant="text"
                                endIcon={
                                    activeStep != 5 ? <ArrowForwardIcon /> : ''
                                }
                                sx={{
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                }}
                            >
                                {activeStep === steps.length - 1 ? '' : 'Nästa'}
                            </Button>
                        </Box>

                        {/* Läkemedel */}
                        {activeStep === 0 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <Visibility
                                        visible={
                                            formRegistrationMethod === 'manual'
                                        }
                                    >
                                        <AvailableDrugs
                                            searchTerm={searchTermDrugs}
                                            setSearchTerm={setSearchTermDrugs}
                                            handleSelect={handleSelectDrug}
                                        />
                                        <Alert
                                            className="mt-6 max-w-2xl mx-6 sm:mx-5 md:mx-1 lg:mx-1"
                                            severity="info"
                                        >
                                            <div>
                                                <div>
                                                    Efter att du har gett
                                                    läkemedel till en patient i
                                                    samband med ett
                                                    ambulansuppdrag, utför
                                                    följande steg:
                                                </div>
                                                <ol>
                                                    <li>1)Välj läkemedel</li>
                                                    <li>
                                                        2) Välj det
                                                        läkemedelsskåp som du
                                                        kommer ta i från för att
                                                        komplettera akutväskan.
                                                        Registrera uttagen mängd
                                                        och behållning efter
                                                        uttag.
                                                    </li>
                                                    <li>
                                                        3) Välj akutväska, och
                                                        registrera behållning
                                                        efter påfyllning.
                                                    </li>
                                                    <li>
                                                        4) Skriv in
                                                        patientuppgifter.
                                                    </li>
                                                    <li>
                                                        5) Vid behov skriv en
                                                        anteckning
                                                    </li>
                                                    <li>
                                                        6) Kontrollera att uttag
                                                        och påfyllning är
                                                        korrect och signera
                                                        transaktionen
                                                    </li>
                                                    <li>
                                                        7) Vid behov, be en
                                                        kollega dubbelsignera
                                                    </li>
                                                </ol>
                                            </div>
                                        </Alert>
                                    </Visibility>
                                </Box>
                                <Visibility
                                    visible={
                                        formRegistrationMethod === 'barcode'
                                    }
                                >
                                    <ScanningDetector
                                        steps={steps}
                                        activeStep={activeStep}
                                        onScanComplete={handleNext}
                                    />
                                </Visibility>
                            </div>
                        )}

                        {/* Läkemedelsskåp */}
                        {activeStep === 1 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <Visibility
                                        visible={
                                            formRegistrationMethod === 'manual'
                                        }
                                    >
                                        <AvailableStorages
                                            label="Uttag från läkemedelsskåp"
                                            searchTerm={searchTermStorage}
                                            setSearchTerm={setSearchTermStorage}
                                            handleSelect={handleSelectStorage}
                                            handleSelectEmergency={
                                                handleSelectEmergency
                                            }
                                            storageType={StorageType.hospital}
                                            transactionType={
                                                TransactionType.AmbulanceTransaction
                                            }
                                        />
                                    </Visibility>
                                </Box>
                                <Visibility
                                    visible={
                                        formRegistrationMethod === 'barcode'
                                    }
                                >
                                    <ScanningDetector
                                        steps={steps}
                                        activeStep={activeStep}
                                        onScanComplete={() => {}}
                                    />
                                </Visibility>
                                <Visibility visible={selectedStorage}>
                                    <Box
                                        className="flex mt-4 flex-row space-x-4 justify-center"
                                        sx={{
                                            width: {
                                                sm: '22rem',
                                                xs: '22rem',
                                                lg: '24rem',
                                            },
                                            maxWidth: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Box sx={{ width: '50%' }}>
                                            <IconTextField
                                                type="number"
                                                size="medium"
                                                id="uttag"
                                                label="Uttag"
                                                value={quantityChanged}
                                                onChange={(e) =>
                                                    setQuantityChanged(
                                                        e.target.value
                                                    )
                                                }
                                                fullWidth
                                                variant="outlined"
                                                prefix={
                                                    selectedDrug
                                                        ? selectedDrug.quantity +
                                                          ' ' +
                                                          selectedDrug.quantityUnit +
                                                          ' x'
                                                        : ''
                                                }
                                                disabled={
                                                    ambulanceOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                                aria-label="Enter the amount for uttag"
                                                aria-required="true"
                                            />
                                        </Box>

                                        <Box sx={{ width: '50%' }}>
                                            <TextField
                                                type="number"
                                                id="behallning"
                                                label="Behållning"
                                                value={storageRemaining}
                                                onChange={(e) =>
                                                    setstorageRemaining(
                                                        e.target.value
                                                    )
                                                }
                                                fullWidth
                                                variant="outlined"
                                                disabled={
                                                    ambulanceOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            style={{
                                                                color: '#aaa',
                                                            }}
                                                        >
                                                            {selectedDrug
                                                                ? selectedDrug.quantity +
                                                                  ' ' +
                                                                  selectedDrug.quantityUnit +
                                                                  ' x'
                                                                : ''}
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        textAlign: 'right',
                                                    },
                                                }}
                                                aria-label="Enter the amount for behållning"
                                                aria-required="true"
                                            />
                                        </Box>
                                    </Box>
                                </Visibility>
                            </div>
                        )}

                        {/* Akutväska */}
                        {activeStep === 2 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <Visibility
                                        visible={
                                            formRegistrationMethod === 'manual'
                                        }
                                    >
                                        <AvailableStorages
                                            label="Tillfört till akutväska"
                                            searchTerm={searchTermEmergency}
                                            setSearchTerm={
                                                setSearchTermEmergency
                                            }
                                            handleSelect={handleSelectStorage}
                                            handleSelectEmergency={
                                                handleSelectEmergency
                                            }
                                            storageType={StorageType.ambulance}
                                            transactionType={
                                                TransactionType.ConnectedTransactionCheckIn
                                            }
                                        />
                                    </Visibility>
                                </Box>
                                <Visibility
                                    visible={
                                        formRegistrationMethod === 'barcode'
                                    }
                                >
                                    <ScanningDetector
                                        steps={steps}
                                        activeStep={activeStep}
                                        onScanComplete={() => {}}
                                    />
                                </Visibility>
                                <Visibility visible={selectedEmergency}>
                                    <Box
                                        className="flex mt-4 flex-row space-x-4 justify-center"
                                        sx={{
                                            width: {
                                                sm: '22rem',
                                                xs: '22rem',
                                                lg: '24rem',
                                            },
                                            maxWidth: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Box sx={{ width: '50%' }}>
                                            <IconTextField
                                                size="medium"
                                                id="uttag"
                                                label="Tillfört"
                                                type="number"
                                                value={quantityChanged}
                                                disabled
                                                fullWidth
                                                variant="outlined"
                                                prefix={
                                                    selectedDrug
                                                        ? selectedDrug.quantity +
                                                          ' ' +
                                                          selectedDrug.quantityUnit +
                                                          ' x'
                                                        : ''
                                                }
                                                aria-label="Enter the amount for uttag"
                                                aria-required="true"
                                            />
                                        </Box>
                                        <Box sx={{ width: '50%' }}>
                                            <TextField
                                                id="emergency-behallning"
                                                label="Behållning"
                                                type="number"
                                                value={emergencyRemaining}
                                                onChange={(e) =>
                                                    setemergencyRemaining(
                                                        e.target.value
                                                    )
                                                }
                                                aria-label="Enter the emergency retention amount"
                                                aria-required="true"
                                                fullWidth
                                                variant="outlined"
                                                disabled={
                                                    ambulanceOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            style={{
                                                                color: '#aaa',
                                                            }}
                                                        >
                                                            {selectedDrug
                                                                ? selectedDrug.quantity +
                                                                  ' ' +
                                                                  selectedDrug.quantityUnit +
                                                                  ' x'
                                                                : ''}
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        textAlign: 'right',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Visibility>
                            </div>
                        )}
                        {/* Patient */}
                        {activeStep === 3 && (
                            <Box
                                sx={{
                                    width: { xs: '24rem', lg: '24rem' },
                                    maxWidth: '100%',
                                    mx: 'auto',
                                }}
                            >
                                <Patient />
                            </Box>
                        )}
                        {/* Anteckning */}
                        {activeStep === 4 && (
                            <Box
                                sx={{
                                    width: { xs: '24rem', lg: '24rem' },
                                    maxWidth: '100%',
                                    mx: 'auto',
                                }}
                            >
                                <Note />
                            </Box>
                        )}
                        {/* Summering */}
                        {activeStep === 5 && (
                            <div>
                                <Summary
                                    selectedDrug={selectedDrug}
                                    selectedStorage={selectedStorage}
                                    quantityChanged={quantityChanged}
                                    storageRemaining={storageRemaining}
                                    selectedEmergency={selectedEmergency}
                                    emergencyRemaining={emergencyRemaining}
                                    patientData={patientData}
                                    note={note}
                                    referensData={undefined}
                                    disposedQuantity={0}
                                    disposedCause={''}
                                    transactionType={
                                        TransactionType.AmbulanceTransaction
                                    }
                                />
                                <div>
                                    <Sign
                                        transactionType={
                                            TransactionType.AmbulanceTransaction
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {ambulanceOverview === TransactionOverview.successOverview && (
                <SignedSuccess
                    transactionFormOverview={
                        TransactionType.AmbulanceTransaction
                    }
                />
            )}
        </div>
    );
};

export default ChainedTransaction;
